import $ from "jquery";
import { createTree } from "jquery.fancytree/dist/modules/jquery.fancytree";
import "jquery.fancytree/dist/modules/jquery.fancytree.dnd5";
import "jquery.fancytree/dist/skin-win8/ui.fancytree.min.css";
import "font-awesome/css/font-awesome.css";
import { Resources } from "../../resources/resources";
import { ApiMethods, Events, parseJson } from "./components";
import "../../../lib/jscolor/jscolor";
/* global JSColor:readonly */

function formatArea(area) {
    var supscript = String.fromCharCode("2".charCodeAt(0) + 128);
    var output;
    if (area >= 100000) {
        output = Math.round((area * 10) / 1000000) / 10 + " mm" + supscript;
    } else {
        output = Math.round(area * 10) / 10 + " μm" + supscript;
    }

    return output;
}

export /**
 * Represents a UI component that allows the manipulation of AI analysis annotations.
 * @memberof PMA.UI.Components
 * @alias AIImageAnalysisResults
 * @param {object} options - Configuration options
 * @param {AnnotationManager} options.annotationManager - The annotation manager to use.
 * @param {string|HTMLElement} options.element - Selector or HTML element where the UI will be rendered.
 * @param {function} options.aiMarketplaceContextEndpointFunction - The function that provides the endpoint to fetch AI analysis context, used for linking to the AI service if available in the annotation metadata.
 * @param {string} [options.aiMarketplaceToken] - The token to access the AI marketplace, used for linking to the AI service if available in the annotation metadata.
 * @param {boolean} [options.zoomOnNavigation=false] - Whether or not zoom to or just highlight annotation on focus.
 * @param {boolean} [options.focusOnSelection=false] - Whether or not focus on selected annotation.
 * @param {boolean} [options.fillPoints=false] - Whether or not to fill points in the annotation geometries when navigating to them.
 */
class AIImageAnalysisResults {
    constructor(options) {
        if (!options.annotationManager) {
            console.error("No annotation manager provided!");
            return;
        }

        if (!options.aiMarketplaceContextEndpointFunction) {
            console.error("No AI marketplace context endpoint function provided!");
            return;
        }

        if (!options.element) {
            console.error("No element provided!");
            return;
        }

        if (options.statusElement instanceof HTMLElement) {
            this.element = options.element;
        } else if (typeof options.element == "string") {
            let el = document.querySelector(options.element);

            if (!el) {
                console.error("Invalid selector for element");
                return;
            } else {
                this.element = el;
            }
        } else {
            console.error("Invalid element");
            return;
        }

        this.annotationManager = options.annotationManager;
        this.aiMarketplaceToken = options.aiMarketplaceToken;
        this.aiMarketplaceContextEndpointFunction = options.aiMarketplaceContextEndpointFunction;
        this.zoomOnNavigation = options.zoomOnNavigation === true;
        this.focusOnSelection = options.focusOnSelection === true;
        this.fillPoints = options.fillPoints === true;
        this.fillPointsTransformer = {
            id: "fill-ai-analysis-points",
            transformerFunction: function (annotation) {
                if (!!annotation.Classification && annotation.DrawingType.toLowerCase() === "point") {
                    annotation.FillColor = annotation.Color;
                }
                return annotation;
            },
        };

        if (this.fillPoints) {
            this.annotationManager.viewport.addAnnotationsTransformer(this.fillPointsTransformer);
        }

        this.id = Array.from(Array(20), () => Math.floor(Math.random() * 36).toString(36)).join("");
        this.navigationCurrentAnnotation = null;
        this.navigationAnnotations = [];
        this.buttonClass = options.buttonClass ? options.buttonClass : "";

        $(this.element).append(`<div class="pma-ui-ai-image-analysis image-analysis-${this.id}">
            <div style="position: relative; width: 100%; text-align: center;">
                <h4 style="width: 100%; text-align: center;">AI Image Analysis Results</h4>
                <button type="button" class="copy-ai-results-btn btn btn-secondary" title="Copy AI analysis results"
                    style="position: absolute; right: 8px; top: 5px; border: 1px solid; cursor: pointer; line-height: 1; background: white !important; color: black !important;">
                    <i class="fa fa-copy" ></i>
                </button>
            </div>
            <hr style="width: 80%; margin: 0 auto; margin-bottom: 8px;">
            <div class="ai-analysis-tree" style="flex: 1; overflow: auto;"></div>
        </div>`);

        // // Uncomment the following lines if you want to add navigation buttons
        // $(this.element).append(`<div class="pma-ui-ai-image-analysis image-analysis-${this.id}">
        // <h4 style="width: 100%; text-align: center;">AI Image Analysis Results</h4>
        // <hr style="width: 80%;margin: 0 auto; margin-bottom: 8px;">
        // <div class="ai-analysis-tree" style="flex: 1; overflow: auto;"></div>
        // <div class="ai-analysis-navigation" style="display: flex; padding: 8px; border-top: 1px solid #0001; margin-top: 8px; align-items: center;">
        // <button class="navigation-btn navigate-previous-btn ${this.buttonClass}" style="width: 25%; margin: 0 8px;">Previous</button>
        // <div class="navigate-info" style="width: 50%; display: flex; justify-content: center; align-items: center; margin: 0 8px; text-align: center;">No annotation is currently focused</div>
        // <button class="navigation-btn navigate-next-btn ${this.buttonClass}" style="width: 25%; margin: 0 8px;">Next</button>
        // </div>
        // </div>`);

        const self = this;
        this.analysisSummary = [];
        let sourceData = [];

        function getContextValue(context, key) {
            if (context[key] !== null && context[key] !== undefined) {
                return context[key];
            }

            return null;
        }

        function formatAnalysisDate(analysisOn) {
            if (!analysisOn) {
                return "";
            }

            try {
                return new Date(analysisOn).toLocaleString("en-GB", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                });
            } catch {
                return "";
            }
        }

        function parseMarketplaceClassificationColors(rawClassifications) {
            const result = new Map();

            if (!rawClassifications) {
                return result;
            }

            let parsedClassifications = rawClassifications;

            if (typeof parsedClassifications === "string") {
                try {
                    parsedClassifications = JSON.parse(parsedClassifications);
                } catch {
                    return result;
                }
            }

            if (!parsedClassifications || typeof parsedClassifications !== "object" || Array.isArray(parsedClassifications)) {
                return result;
            }

            Object.entries(parsedClassifications).forEach(([name, value]) => {
                let color = null;

                if (value && typeof value === "object" && !Array.isArray(value) && typeof value.Color === "string") {
                    color = value.Color;
                }

                if (color) {
                    result.set(name.toLowerCase(), color);
                }
            });

            return result;
        }

        const loadPmaCoreAnnotationsInfo = () => {
            return new Promise((resolve) => {
                const viewport = self.annotationManager.viewport;

                if (!viewport || !viewport.serviceUrl || !viewport.sessionID || !viewport.image) {
                    resolve(null);
                    return;
                }

                const params = new URLSearchParams({
                    sessionID: viewport.sessionID,
                    pathOrUid: viewport.image,
                });

                fetch(`${viewport.serviceUrl}${ApiMethods.GetAnnotationInfo}?${params.toString()}`)
                    .then((response) => {
                        if (!response.ok) {
                            console.warn("Failed to fetch " + ApiMethods.GetAnnotationInfo, response.status);
                            resolve(null);
                            return null;
                        }

                        return response.text();
                    })
                    .then((text) => {
                        if (text === null) {
                            return;
                        }

                        let parsed = null;

                        try {
                            parsed = parseJson(text);
                        } catch (e) {
                            console.warn("Failed to parse " + ApiMethods.GetAnnotationInfo + " response", e);
                            resolve(null);
                            return;
                        }

                        resolve(parsed);
                    })
                    .catch((e) => {
                        console.warn("Failed to fetch " + ApiMethods.GetAnnotationInfo, e);
                        resolve(null);
                    });
            });
        };

        const parsePmaCoreAnnotationsInfo = (entry) => {
            const tasks = new Map();

            if (!entry || !entry.Contexts || typeof entry.Contexts !== "object") {
                return tasks;
            }

            Object.entries(entry.Contexts).forEach(([contextKey, contextValue]) => {
                let parsedContext = null;

                try {
                    parsedContext = JSON.parse(contextKey);
                } catch {
                    return;
                }

                if (!parsedContext || !parsedContext.AITaskId) {
                    return;
                }

                const count = contextValue && typeof contextValue === "object" ? Number(contextValue[parsedContext.AIClassification]) || 0 : 0;

                let task = tasks.get(parsedContext.AITaskId);

                if (!task) {
                    task = {
                        taskId: parsedContext.AITaskId,
                        classifications: new Map(),
                    };

                    tasks.set(parsedContext.AITaskId, task);
                }

                task.classifications.set(parsedContext.AIClassification, count);
            });

            return tasks;
        };

        const fetchMarketplaceContexts = async () => {
            if (typeof self.aiMarketplaceContextEndpointFunction !== "function") {
                return null;
            }

            let endpoint = null;

            try {
                endpoint = self.aiMarketplaceContextEndpointFunction(this.annotationManager.path);
            } catch (e) {
                console.warn("Failed to compute AI context endpoint", e);
                return;
            }

            if (!endpoint) {
                return;
            }

            let response;

            try {
                response = await fetch(
                    endpoint,
                    this.aiMarketplaceToken
                        ? {
                              headers: { Authorization: "Bearer " + this.aiMarketplaceToken },
                          }
                        : undefined,
                );
            } catch (e) {
                console.warn("Failed to fetch additional AI contexts", e);
                return;
            }

            if (response.status === 404) {
                return;
            }

            if (!response.ok) {
                console.warn("Failed to fetch additional AI contexts", response.status);
                return;
            }

            try {
                const contexts = await response.json();
                return Array.isArray(contexts) ? contexts : [];
            } catch (e) {
                console.warn("Failed to parse additional AI contexts", e);
                return;
            }
        };

        const buildSourceData = (pmaTasks, marketplaceContexts) => {
            const result = [];
            self.analysisSummary = [];

            const aiTaskById = new Map();

            if (Array.isArray(marketplaceContexts)) {
                marketplaceContexts.forEach((context) => {
                    if (!context || typeof context !== "object") {
                        return;
                    }

                    const id = getContextValue(context, "aiTaskId");

                    if (id !== null && id !== undefined) {
                        aiTaskById.set(String(id), context);
                    }
                });
            }

            const marketplaceConfigured = marketplaceContexts !== null;

            pmaTasks.forEach((task, taskId) => {
                const marketplaceContext = aiTaskById.get(String(taskId));

                if (!marketplaceConfigured || !marketplaceContext) {
                    return;
                }

                const getMetadata = (contextKey) => {
                    if (marketplaceContext[contextKey] !== null && marketplaceContext[contextKey] !== undefined) {
                        return marketplaceContext[contextKey];
                    }

                    return null;
                };

                const vendor = getMetadata("vendor");
                const analysisOn = getMetadata("analysisOn");
                const service = getMetadata("service");
                const model = getMetadata("model");
                const analysisType = getMetadata("analysisType");
                const scenarioType = getMetadata("scenarioType");
                const scenarioVersion = getMetadata("scenarioVersion");
                const positivityThreshold = getMetadata("positivityThreshold");
                const proportionScoreFloat = getMetadata("proportionScoreFloat");
                const proportionScore = getMetadata("proportionScore");
                const intensityScore = getMetadata("intensityScore");
                const her2Score = getMetadata("her2Score");
                const tumorProportionScoreFloat = getMetadata("tumorProportionScoreFloat");
                const combinedPositiveScoreFloat = getMetadata("combinedPositiveScoreFloat");
                const proliferationScore = getMetadata("proliferationScore");
                const classificationColors = parseMarketplaceClassificationColors(getContextValue(marketplaceContext, "classifications"));
                const analysisDate = formatAnalysisDate(analysisOn);
                const analysisKey = (model || service || taskId) + "_" + (analysisOn || "unknown");
                const classificationEntries = [];
                const summaryClassifications = [];

                task.classifications.forEach((totalPoints, className) => {
                    const classificationColor = classificationColors.get(className.toLowerCase()) || "";

                    classificationEntries.push({
                        title: `<strong style="text-transform: capitalize;">${className}</strong> (${totalPoints})
                            <span class="color-switcher-wrapper">
                                <button class="color-switcher" data-value="${classificationColor}"
                                    style="height: 19px; padding: 0; margin: 0; border: none; border-radius: 25%; margin-left: 4px;">
                                </button>
                            </span>
                            <span class="color-reset-wrapper">
                                <button class="color-reset hidden" title="Reset color" data-value="${classificationColor}"
                                    style="height: 19px; padding: 0; margin: 0; border: none; border-radius: 25%; margin-left: 4px; background: transparent;">
                                    <i class="fa ga-lg fa-undo" aria-hidden="true"></i>
                                </button>
                            </span>`,
                        key: analysisKey + "_" + className,
                        id: taskId,
                        taskId: taskId,
                        classification: className,
                        type: "classification",
                        selected: true,
                        unselectable: false,
                        summary: {
                            name: className,
                            count: totalPoints,
                            color: classificationColor,
                            points: totalPoints,
                        },
                    });

                    summaryClassifications.push({
                        name: className,
                        count: totalPoints,
                        color: classificationColor,
                        points: totalPoints,
                    });
                });

                self.analysisSummary.push({
                    aiTaskId: taskId,
                    vendor: vendor,
                    analysisOn: analysisOn,
                    analysisType: analysisType,
                    service: service,
                    scenarioType: scenarioType,
                    scenarioVersion: scenarioVersion,
                    positivityThreshold: positivityThreshold,
                    proportionScoreFloat: proportionScoreFloat,
                    proportionScore: proportionScore,
                    intensityScore: intensityScore,
                    her2Score: her2Score,
                    tumorProportionScoreFloat: tumorProportionScoreFloat,
                    combinedPositiveScoreFloat: combinedPositiveScoreFloat,
                    proliferationScore: proliferationScore,
                    classifications: summaryClassifications,
                });

                result.push({
                    title: `<strong>${vendor || "Unknown vendor"}${analysisDate ? " on " + analysisDate : ""}</strong>
                <div class="pma-ui-ai-analysis-info">
                ${analysisType ? "Analysis type: <i>" + analysisType + "</i><br />" : ""}
                ${service ? "AI service: <i>" + service + "</i><br />" : ""}
                ${scenarioType ? "AI scenario: <i>" + scenarioType + (scenarioVersion ? ` (v.${scenarioVersion})` : "") + "</i><br />" : ""}
                ${positivityThreshold !== null && positivityThreshold !== undefined ? "Positivity threshold: <i>" + positivityThreshold + "</i><br />" : ""}
                ${proportionScoreFloat !== null && proportionScoreFloat !== undefined ? "Proportion score (float): <i>" + proportionScoreFloat + "</i><br />" : ""}
                ${proportionScore !== null && proportionScore !== undefined ? "Proportion score: <i>" + proportionScore + "</i><br />" : ""}
                ${intensityScore !== null && intensityScore !== undefined ? "Intensity score: <i>" + intensityScore + "</i><br />" : ""}
                ${her2Score !== null && her2Score !== undefined ? "HER2 score: <i>" + her2Score + "</i><br />" : ""}
                ${tumorProportionScoreFloat !== null && tumorProportionScoreFloat !== undefined ? "Tumor proportion score (float): <i>" + tumorProportionScoreFloat + "</i><br />" : ""}
                ${combinedPositiveScoreFloat !== null && combinedPositiveScoreFloat !== undefined ? "Combined positive score (float): <i>" + combinedPositiveScoreFloat + "</i><br />" : ""}
                ${proliferationScore !== null && proliferationScore !== undefined ? "Proliferation score: <i>" + proliferationScore + "</i>" : ""}
                </div>`,
                    key: analysisKey,
                    id: taskId,
                    taskId: taskId,
                    classification: null,
                    type: "analysis",
                    selected: true,
                    unselectable: false,
                    children: classificationEntries,
                });
            });

            return result;
        };

        const initializeFancytree = async () => {
            const annotationsInfo = await loadPmaCoreAnnotationsInfo();
            const pmaTasks = parsePmaCoreAnnotationsInfo(annotationsInfo);
            const marketplaceContexts = await fetchMarketplaceContexts();

            sourceData = buildSourceData(pmaTasks, marketplaceContexts);

            self.fancytree = createTree(`.image-analysis-${self.id} .ai-analysis-tree`, {
                checkbox: true,
                selectMode: 3,
                toggleEffect: false,
                source: sourceData,
                select: function (event, data) {
                    const nodesToExclude = data.tree.findAll((n) => n.data && n.data.taskId && n.data.classification && !n.isSelected());

                    if (nodesToExclude.length === 0) {
                        self.annotationManager.viewport.options.annotations.excludedContexts = null;
                    } else {
                        self.annotationManager.viewport.options.annotations.excludedContexts = nodesToExclude.map(
                            (n) => `{"AITaskId":"${n.data.taskId}","AIClassification":"${n.data.classification}"}`,
                        );
                    }

                    self.annotationManager.viewport.reloadAnnotations();
                },
                icon: function (event, data) {
                    if (data.node.type === "analysis" || data.node.type === "classification" || data.node.type === "annotation") {
                        return "fa fa-lg fa-crosshairs highlighter";
                    } else {
                        return "";
                    }
                },
                createNode: function (event, data) {
                    const ht = $(data.node.li).find(".highlighter");

                    if (ht) {
                        ht.off("mouseenter");
                        ht.off("mouseleave");
                        ht.on("mouseenter", (e) => {
                            if (!data.node.data.id.length || data.node.data.id.length === 0) {
                                if (data.node.data.taskId) {
                                    if (data.node.data.classification) {
                                        const features = self.annotationManager.viewport.annotationsLayer
                                            .getSource()
                                            .getFeatures()
                                            .filter((f) => {
                                                return (
                                                    f.metaData.Context &&
                                                    f.metaData.Classification &&
                                                    f.metaData.Context.indexOf(data.node.data.taskId) !== -1 &&
                                                    f.metaData.Classification.toLowerCase() === data.node.data.classification.toLowerCase()
                                                );
                                            });

                                        if (features.length) {
                                            features.forEach((f) => {
                                                self.annotationManager.hoverInteraction.getFeatures().push(f);
                                            });
                                        }
                                    } else {
                                        const features = self.annotationManager.viewport.annotationsLayer
                                            .getSource()
                                            .getFeatures()
                                            .filter((f) => {
                                                return f.metaData.Context && f.metaData.Context.indexOf(data.node.data.taskId) !== -1;
                                            });

                                        if (features.length) {
                                            features.forEach((f) => {
                                                self.annotationManager.hoverInteraction.getFeatures().push(f);
                                            });
                                        }
                                    }
                                }
                            } else {
                                data.node.data.id.forEach((id) => {
                                    var f = self.annotationManager.viewport.annotationsLayer.getSource().getFeatureById(id);
                                    if (f) {
                                        var feats = self.annotationManager.hoverInteraction.getFeatures();
                                        feats.push(f);
                                    }
                                });
                            }
                        });

                        ht.on("mouseleave", (e) => {
                            self.annotationManager.hoverInteraction.getFeatures().clear();
                        });
                    }

                    const swr = $(data.node.li).find(".color-switcher");
                    const cr = $(data.node.li).find(".color-reset");

                    if (swr && swr.length > 0) {
                        var myPicker = new JSColor(swr[0], {
                            format: "hexa",
                            value: $(swr).data("value"),
                            closable: true,
                            previewSize: 19,
                            closeText: Resources.translate("Close"),
                            onChange: () => {
                                if (cr && cr.length > 0) {
                                    cr.removeClass("hidden");
                                }

                                if (data.node.data.id && data.node.data.id.length > 0) {
                                    const annots = self.annotationManager.viewport
                                        .getAnnotations()
                                        .filter((x) => data.node.data.id.toString().includes(x.getId().toString()));
                                    annots.forEach((ann) => {
                                        const md = ann.metaData;
                                        md.Color = swr[0].jscolor.targetElement.dataset.currentColor;
                                        self.annotationManager.setMetadata(ann, md);
                                    });

                                    self.annotationManager.fireEvent(Events.AnnotationModified, null);
                                } else {
                                    if (!data.node.data.taskId || !data.node.data.classification) {
                                        return;
                                    }

                                    const annotationsTransformer = {
                                        id: `${data.node.data.taskId}_${data.node.data.classification}_color_transformer`,
                                        transformerFunction: (annotation) => {
                                            let taskId = data.node.data.taskId;

                                            if (
                                                annotation.Classification &&
                                                annotation.Classification.toLowerCase() === data.node.data.classification.toLowerCase() &&
                                                annotation.Context &&
                                                annotation.Context.indexOf(taskId) !== -1
                                            ) {
                                                const currentColor = annotation.Color;

                                                annotation.Color = swr[0].jscolor.targetElement.dataset.currentColor;

                                                if (annotation.FillColor === currentColor) {
                                                    annotation.FillColor = annotation.Color;
                                                }
                                            }

                                            return annotation;
                                        },
                                    };

                                    self.annotationManager.viewport.addAnnotationsTransformer(annotationsTransformer);
                                }
                            },
                        });

                        if (cr && cr.length > 0) {
                            cr.off("click");
                            cr.on("click", (e) => {
                                let oldColor = $(e.currentTarget).data("value");

                                if (/^#([0-9A-Fa-f]{6})$/.test(oldColor)) {
                                    oldColor += "ff";
                                }

                                myPicker.fromString(oldColor);
                                $(e.currentTarget).addClass("hidden");

                                if (data.node.data.id && data.node.data.id.length > 0) {
                                    const annots = self.annotationManager.viewport
                                        .getAnnotations()
                                        .filter((x) => data.node.data.id.toString().includes(x.getId().toString()));
                                    annots.forEach((ann) => {
                                        const md = ann.metaData;
                                        md.Color = oldColor;
                                        self.annotationManager.setMetadata(ann, md);
                                    });
                                    self.annotationManager.fireEvent(Events.AnnotationModified, null);
                                } else {
                                    if (!data.node.data.taskId || !data.node.data.classification) {
                                        return;
                                    }

                                    const transformerId = `${data.node.data.taskId}_${data.node.data.classification}_color_transformer`;
                                    self.annotationManager.viewport.removeAnnotationsTransformer(transformerId);
                                }
                            });
                        }
                    }
                },
            });
        };

        initializeFancytree();

        function navigatePrevious() {
            if (!self.fancytree || !self.navigationCurrentAnnotation || !self.navigationCurrentAnnotation) {
                return;
            }

            var curIdx = self.navigationAnnotations.findIndex((x) => x === self.navigationCurrentAnnotation.getId());
            var previousIdx = curIdx - 1;

            if (curIdx === 0) {
                previousIdx = self.navigationAnnotations.length - 1;
            }

            self.navigationCurrentAnnotation = self.annotationManager.viewport
                .getAnnotations()
                .find((x) => x.getId() === self.navigationAnnotations[previousIdx]);

            while (
                self.annotationManager.viewport.hiddenAnnotations &&
                self.annotationManager.viewport.hiddenAnnotations.findIndex((x) => x.getId() === self.navigationCurrentAnnotation.getId()) !== -1
            ) {
                if (curIdx === previousIdx) {
                    $(`.image-analysis-${self.id} .navigate-info`).text("No annotation is currently focused");
                    return;
                }

                previousIdx--;

                if (previousIdx === -1) {
                    previousIdx = self.navigationAnnotations.length - 1;
                }

                self.navigationCurrentAnnotation = self.annotationManager.viewport
                    .getAnnotations()
                    .find((x) => x.getId() === self.navigationAnnotations[previousIdx]);
            }

            var c = self.fancytree.findAll((n) => {
                return n.extraClasses === "current-node";
            });

            c.forEach((cn) => {
                cn.extraClasses = "";
                cn.render();
            });

            var n = self.fancytree.findFirst((n) => {
                return n.key.toString() === self.navigationCurrentAnnotation.getId().toString();
            });

            if (!n) {
                return;
            }

            n.extraClasses = "current-node";
            n.render();
            n.li.scrollIntoView({ block: "center", inline: "nearest" });

            $(`.image-analysis-${self.id} .navigate-info`).text(self.navigationCurrentAnnotation.metaData.Classification + " " + (previousIdx + 1));
            self.annotationManager.viewport.focusToAnnotation(self.navigationCurrentAnnotation.getId(), self.zoomOnNavigation, [50, 50, 50, 50]);
            self.annotationManager.highlightAnnotation(self.navigationCurrentAnnotation.getId());
        }

        function navigateNext() {
            if (!self.fancytree || !self.navigationCurrentAnnotation || !self.navigationCurrentAnnotation) {
                return;
            }

            var curIdx = self.navigationAnnotations.findIndex((x) => x === self.navigationCurrentAnnotation.getId());
            var nextIdx = curIdx + 1;

            if (curIdx === self.navigationAnnotations.length - 1) {
                nextIdx = 0;
            }

            self.navigationCurrentAnnotation = self.annotationManager.viewport.getAnnotations().find((x) => x.getId() === self.navigationAnnotations[nextIdx]);

            while (
                self.annotationManager.viewport.hiddenAnnotations &&
                self.annotationManager.viewport.hiddenAnnotations.findIndex((x) => x.getId() === self.navigationCurrentAnnotation.getId()) !== -1
            ) {
                if (curIdx === nextIdx) {
                    $(`.image-analysis-${self.id} .navigate-info`).text("No annotation is currently focused");
                    return;
                }

                nextIdx++;

                if (nextIdx === self.navigationAnnotations.length) {
                    nextIdx = 0;
                }

                self.navigationCurrentAnnotation = self.annotationManager.viewport
                    .getAnnotations()
                    .find((x) => x.getId() === self.navigationAnnotations[nextIdx]);
            }

            var c = self.fancytree.findAll((n) => {
                return n.extraClasses === "current-node";
            });

            c.forEach((cn) => {
                cn.extraClasses = "";
                cn.render();
            });

            var n = self.fancytree.findFirst((n) => {
                return n.key.toString() === self.navigationCurrentAnnotation.getId().toString();
            });

            if (!n) {
                return;
            }

            n.extraClasses = "current-node";
            n.render();
            n.li.scrollIntoView({ block: "center", inline: "nearest" });

            $(`.image-analysis-${self.id} .navigate-info`).text(self.navigationCurrentAnnotation.metaData.Classification + " " + (nextIdx + 1));
            self.annotationManager.viewport.focusToAnnotation(self.navigationCurrentAnnotation.getId(), self.zoomOnNavigation, [50, 50, 50, 50]);
            self.annotationManager.highlightAnnotation(self.navigationCurrentAnnotation.getId());
        }

        function getResultsAsText() {
            if (!self.analysisSummary || self.analysisSummary.length === 0) {
                return "";
            }

            const lines = [];

            self.analysisSummary.forEach((analysis, idx) => {
                lines.push("");

                const analysisDate = analysis.analysisOn
                    ? new Date(analysis.analysisOn).toLocaleString("en-GB", {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                      })
                    : "";

                lines.push((analysis.vendor || "Unknown vendor") + (analysisDate ? " on " + analysisDate : ""));

                if (analysis.analysisType) {
                    lines.push("Analysis type: " + analysis.analysisType);
                }

                if (analysis.service) {
                    lines.push("AI service: " + analysis.service);
                }

                if (analysis.scenarioType) {
                    lines.push("AI scenario: " + analysis.scenarioType + (analysis.scenarioVersion ? " (v." + analysis.scenarioVersion + ")" : ""));
                }

                if (analysis.positivityThreshold !== null && analysis.positivityThreshold !== undefined) {
                    lines.push("Positivity threshold: " + analysis.positivityThreshold);
                }

                if (analysis.proportionScoreFloat !== null && analysis.proportionScoreFloat !== undefined) {
                    lines.push("Proportion score (float): " + analysis.proportionScoreFloat);
                }

                if (analysis.proportionScore !== null && analysis.proportionScore !== undefined) {
                    lines.push("Proportion score: " + analysis.proportionScore);
                }

                if (analysis.intensityScore !== null && analysis.intensityScore !== undefined) {
                    lines.push("Intensity score: " + analysis.intensityScore);
                }

                if (analysis.her2Score !== null && analysis.her2Score !== undefined) {
                    lines.push("HER2 score: " + analysis.her2Score);
                }

                if (analysis.tumorProportionScoreFloat !== null && analysis.tumorProportionScoreFloat !== undefined) {
                    lines.push("Tumor proportion score (float): " + analysis.tumorProportionScoreFloat);
                }

                if (analysis.combinedPositiveScoreFloat !== null && analysis.combinedPositiveScoreFloat !== undefined) {
                    lines.push("Combined positive score (float): " + analysis.combinedPositiveScoreFloat);
                }

                if (analysis.proliferationScore !== null && analysis.proliferationScore !== undefined) {
                    lines.push("Proliferation score: " + analysis.proliferationScore);
                }

                if (analysis.classifications && analysis.classifications.length > 0) {
                    analysis.classifications.forEach((c) => {
                        lines.push("- " + c.name + " (" + c.count + ")");
                        if (c.points > 1) {
                            lines.push("  No of points: " + c.points);
                        }
                    });
                }

                if (idx < self.analysisSummary.length - 1) {
                    lines.push("");
                }
            });

            return lines.join("\n").trim();
        }

        async function copyResults() {
            const text = getResultsAsText();

            if (!text) {
                console.warn("No AI analysis results available to copy");
                return false;
            }

            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch (e) {
                console.error("Failed to copy AI analysis results", e);
                return false;
            }
        }

        function showCopyFeedback(success) {
            const btn = $(`.image-analysis-${self.id} .copy-ai-results-btn`);

            if (!btn.length) {
                return;
            }

            btn.attr("disabled", true);
            btn.css("opacity", 0.75);

            if (success) {
                btn.find("i").attr("class", "fa fa-check");
            } else {
                btn.find("i").attr("class", "fa fa-exclamation-triangle");
            }

            if (self.copyFeedbackTimeout) {
                clearTimeout(self.copyFeedbackTimeout);
            }

            self.copyFeedbackTimeout = setTimeout(() => {
                btn.find("i").attr("class", "fa fa-copy");
                btn.attr("disabled", false);
                btn.css("opacity", "");
            }, 1500);
        }

        $(`.image-analysis-${this.id} .copy-ai-results-btn`).off("click");
        $(`.image-analysis-${this.id} .copy-ai-results-btn`).on("click", async () => {
            const res = await copyResults();
            showCopyFeedback(res);
        });

        $(`.image-analysis-${this.id} .navigate-previous-btn`).on("click", navigatePrevious);
        $(`.image-analysis-${this.id} .navigate-next-btn`).on("click", navigateNext);

        this.annotationManager.listen("annotationsSelectionChanged", () => {
            if (!self.fancytree) {
                return;
            }

            var sel = self.annotationManager.getSelection();

            if (!sel || sel.length === 0) {
                return;
            }

            var c = self.fancytree.findAll((n) => {
                return n.extraClasses === "current-node";
            });

            c.forEach((cn) => {
                cn.extraClasses = "";
                cn.render();
            });

            var n = self.fancytree.findFirst((n) => {
                return n.key.toString() === sel[0].getId().toString();
            });

            if (!n) {
                $(`.image-analysis-${self.id} .navigate-info`).text("No annotation is currently focused");
                return;
            }

            n.extraClasses = "current-node";
            n.render();
            n.makeVisible({ scrollIntoView: true }).then(() => {
                n.li.scrollIntoView({ block: "center", inline: "nearest" });
            });

            self.navigationCurrentAnnotation = sel[0];
            self.navigationAnnotations = n.parent.data.id;

            var curIdx = n.parent.data.id.findIndex((x) => self.navigationCurrentAnnotation.getId() === x);

            $(`.image-analysis-${self.id} .navigate-info`).text(self.navigationCurrentAnnotation.metaData.Classification + " " + (curIdx + 1));

            if (self.focusOnSelection) {
                self.annotationManager.viewport.focusToAnnotation(self.navigationCurrentAnnotation.getId(), self.zoomOnNavigation, [50, 50, 50, 50]);
                self.annotationManager.highlightAnnotation(self.navigationCurrentAnnotation.getId());
            }
        });
    }

    /**
     * Gets the state of the zoomOnNavigation option
     * @returns {boolean}
     */
    getZoomOnNavigation() {
        return this.zoomOnNavigation;
    }

    /**
     * Toggles the zoomOnNavigation option
     * @param {boolean} zoomOnNavigation - Whether to zoom when navigating to an annotation
     */
    setZoomOnNavigation(zoomOnNavigation) {
        this.zoomOnNavigation = zoomOnNavigation === true;
    }

    /**
     * Gets the state of the focusOnSelection option
     * @returns {boolean}
     */
    getFocusOnSelection() {
        return this.focusOnSelection;
    }

    /**
     * Toggles the focusOnSelection option
     * @param {boolean} focusOnSelection - Whether to focus on the selected annotation when the selection changes
     */
    setFocusOnSelection(focusOnSelection) {
        this.focusOnSelection = focusOnSelection === true;
    }

    /**
     * Gets the fill points for the AI analysis annotations
     * @returns {boolean}
     */
    getFillPoints() {
        return this.fillPoints;
    }

    /**
     * Toggles the fillPoints option for the AI analysis annotations
     * @param {boolean} fillPoints - Whether to fill the points for the AI analysis annotations
     */
    setFillPoints(fillPoints) {
        this.fillPoints = fillPoints === true;

        if (fillPoints === true) {
            this.annotationManager.viewport.addAnnotationsTransformer(this.fillPointsTransformer);
        } else {
            this.annotationManager.viewport.removeAnnotationsTransformer(this.fillPointsTransformer.id);
        }
    }
}
